import { sortBy, uniq } from "lodash";
import * as Sentry from "@sentry/browser";

/*
 * If single-spa is available, we'll add the activeApps to each logged message/exception
 */
let singleSpa;
// if featureToggles is available, we'll add the toggle information to the logs
let featureToggles;

export function init() {
  setTimeout(() => {
    // The SystemJS branch asks the registry whether single-spa is already loaded, so a
    // page without it is left alone. The native registry cannot be queried, but single-spa
    // is a common dependency and always present in the ESM import map, so the ESM branch
    // imports it directly and lets the catch below handle a page that lacks it.
    const singleSpaPromise =
      process.env.ESM_BUILD === "true"
        ? import("single-spa")
        : SystemJS.has(SystemJS.normalizeSync("single-spa"))
          ? SystemJS.import("single-spa")
          : null;

    if (singleSpaPromise) {
      singleSpaPromise
        .then((sspa) => {
          singleSpa = sspa;
        })
        .catch((ex) => {
          console.error(
            `Cannot initialize sentry because could not import single-spa`
          );
          setTimeout(() => {
            throw ex;
          });
        });
    }
    (process.env.ESM_BUILD === "true"
      ? import("@canopytax/feature-toggles")
      : SystemJS.import("feature-toggles!sofe")
    ).then((ft) => (featureToggles = ft));
  });

  window.addEventListener("single-spa:routing-event", () => {
    if (singleSpa) {
      Sentry.getCurrentScope().setTag(
        "activeApps",
        stringifyMountedApps(singleSpa.getMountedApps())
      );
    }
  });
}

export function sentryLoaded(scope) {
  setTimeout(() => {
    if (singleSpa) {
      scope.setTag(
        "activeApps",
        stringifyMountedApps(singleSpa.getMountedApps())
      );
    }
  });
}

export function addActiveApps(obj) {
  if (singleSpa) {
    obj.activeApps = stringifyMountedApps(singleSpa.getMountedApps());
  }
}

export function getFeatureToggleInformation() {
  if (featureToggles && featureToggles.getAllToggleInformationForSentry) {
    return featureToggles.getAllToggleInformationForSentry();
  }
}

export function getSingleSpaVersioningInformation(
  manifest = {},
  identifiedService = ""
) {
  const alwaysServices = ["app-loader-ui", "fetcher"];
  let activeApps = [];
  if (singleSpa) {
    activeApps = singleSpa.getMountedApps();
  }

  const exceptionServices = [
    ...alwaysServices,
    ...activeApps,
    identifiedService,
  ];
  return uniq(exceptionServices).reduce((acc, service) => {
    const normalized = normalizeServiceName(service);
    const info = getServiceVersionInfo(manifest, normalized);
    if (info) {
      acc[normalized] = info;
    }
    return acc;
  }, {});
}

// Some call sites (notably getAssociatedService) yield a manifest key like
// `@canopytax/tasks-ui` or `tasks-ui!sofe` rather than a bare service name.
// Strip those affixes so lookups and output keys are consistent.
function normalizeServiceName(service) {
  if (!service) return service;
  if (service.startsWith("@canopytax/")) return service.slice(11);
  if (service.endsWith("!sofe")) return service.slice(0, -5);
  return service;
}

// Expects a bare service name. Prefers the `@canopytax/${service}` manifest
// key (the modern form) and falls back to the legacy `${service}!sofe` key.
// Both currently point at the same URL. Callers that may have a prefixed or
// suffixed input must run it through normalizeServiceName first.
export function getServiceVersionInfo(manifest, service) {
  if (!service) return undefined;
  const url = manifest[`@canopytax/${service}`] ?? manifest[`${service}!sofe`];
  if (!url) return undefined;
  // Dev override (e.g. https://localhost:8080/forms-ui.js) — flag as
  // "localHost" so the service still shows up with a clear marker rather
  // than a noisy dev URL.
  if (url.includes("localhost")) return "localHost";
  return url;
}

// Pulls the build hash out of a manifest entry (URL), or returns "localHost"
// for dev-override markers. Used by Sentry tags that want a compact filterable
// value while the extras hold the full URL.
export function getHashFromManifestEntry(entry) {
  if (!entry) return undefined;
  if (entry === "localHost") return "localHost";
  // CDN URL: https://.../<sofe|esm>/<service>/<hash>/<file>.js
  const match = entry.match(/\/(?:sofe|esm)\/[^/]+\/([^/]+)\//);
  return match ? match[1] : undefined;
}

function stringifyMountedApps(mountedApps) {
  return JSON.stringify(sortBy(mountedApps));
}
